/**
 * Media Upload Service
 * Handles file uploads and media management
 */

import { config } from "../config/config";

export const mediaService = {
  /**
   * Upload a single file
   * @param {File} file - The file to upload
   * @param {string} directory - Directory to store file (optional)
   * @returns {Promise} Upload result
   */
  uploadFile: async (file, directory = "general") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("directory", directory);

    try {
      const response = await fetch(`${config.api.baseURL}/admin/media/upload`, {
        method: "POST",
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  },

  /**
   * Upload multiple files
   * @param {FileList} files - Files to upload
   * @param {string} directory - Directory to store files
   * @returns {Promise} Upload results
   */
  uploadMultiple: async (files, directory = "general") => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append("files", file);
    });
    formData.append("directory", directory);

    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/media/upload-multiple`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Multiple file upload error:", error);
      throw error;
    }
  },

  /**
   * Get media list for a directory
   * @param {string} directory - Directory to list
   * @returns {Promise} List of media files
   */
  getMediaList: async (directory = "general") => {
    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/media/list?directory=${directory}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch media list");
      }

      const json = await response.json();
      // API returns { success, directory, files }
      return Array.isArray(json.files) ? json.files : [];
    } catch (error) {
      console.error("Error fetching media list:", error);
      throw error;
    }
  },

  /**
   * Delete a media file
   * @param {string} filePath - Path to file to delete
   * @returns {Promise} Deletion result
   */
  deleteFile: async (filePath) => {
    try {
      const response = await fetch(`${config.api.baseURL}/admin/media/delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ filePath }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      return await response.json();
    } catch (error) {
      console.error("Error deleting file:", error);
      throw error;
    }
  },

  /**
   * Get file URL
   * @param {string} filePath - Path to file
   * @returns {string} Full URL to file
   */
  getFileUrl: (filePath) => {
    if (!filePath) return "";
    if (filePath.startsWith("http")) return filePath;

    const baseURL = config.api.baseURL.replace("/api", "");
    return `${baseURL}/media/${filePath}`;
  },
};

export default mediaService;
